'use client';

import { useEffect, useRef } from 'react';
import styles from './Trace.module.css';

export const ecgSignal =
  (bpm: number) =>
  (t: number): number => {
    const beat = 60 / Math.max(1, bpm);
    const x = t % beat;
    if (x < 0.1 * beat)
      return 0.5 + 0.1 * Math.sin((Math.PI / (0.1 * beat)) * x);
    if (x < 0.15 * beat) {
      const p = (x - 0.1 * beat) / (0.05 * beat);
      return p < 0.5 ? 0.5 - p : 0.5 + (1 - Math.abs(2 * p - 1)) * 1.5;
    }
    if (x < 0.25 * beat) {
      const p = (x - 0.15 * beat) / (0.1 * beat);
      return 0.5 + 0.15 * Math.sin(Math.PI * p);
    }
    return 0.5;
  };

export const Trace = ({
  sample,
  height = 32,
  color = 'rgba(255, 255, 255, 0.9)',
  pointsPerSecond = 120,
  className,
  ariaLabel,
}: {
  sample: (tSeconds: number) => number;
  height?: number;
  color?: string;
  pointsPerSecond?: number;
  className?: string;
  ariaLabel?: string;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sampleRef = useRef(sample);
  sampleRef.current = sample;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;

    let width = 0;
    const resize = () => {
      width = canvas.clientWidth;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const spacing = 0.5; // px per point
    let values: number[] = [];
    let produced = 0;
    let raf = 0;
    const startedAt = performance.now();

    const frame = (now: DOMHighResTimeStamp) => {
      raf = requestAnimationFrame(frame);
      const total = Math.floor(((now - startedAt) / 1000) * pointsPerSecond);
      if (total === produced) return;
      for (let i = produced; i < total; i++) {
        values.push(sampleRef.current(i / pointsPerSecond));
      }
      produced = total;
      const maxPoints = Math.ceil(width / spacing);
      if (values.length > maxPoints) values = values.slice(-maxPoints);

      ctx.clearRect(0, 0, width, height);
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let i = 0; i < values.length; i++) {
        const x = width - (values.length - i) * spacing;
        const y =
          (1 - Math.min(1, Math.max(0, values[i]))) * (height - 1) + 0.5;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [height, color, pointsPerSecond]);

  return (
    <canvas
      ref={canvasRef}
      className={`${styles.canvas}${className ? ` ${className}` : ''}`}
      style={{ height }}
      role="img"
      aria-label={ariaLabel ?? 'Live signal'}
    />
  );
};
